import { forwardRef, type PropsWithChildren } from "react";

import ChevronIcon from "$/lib/icons/ChevronIcon";
import { cn } from "$/lib/utils/functions/misc.functions";
import { valueOrNothing } from "$/lib/utils/functions/util.functions";

import { PaginationButton } from "../../atoms";
import type { PaginationProps } from "./Pagination.types";

function generatePageNumbers(currentPage: number, totalNumberOfPages: number) {
  if (totalNumberOfPages <= 3) {
    return Array.from({ length: totalNumberOfPages }, (_, index) => index + 1);
  }
  if (currentPage === totalNumberOfPages) {
    return [totalNumberOfPages - 2, totalNumberOfPages - 1, totalNumberOfPages];
  }

  if (currentPage === 1) {
    return [currentPage, currentPage + 1, currentPage + 2];
  }

  return [currentPage - 1, currentPage, currentPage + 1];
}

const Pagination = forwardRef<
  HTMLDivElement,
  PropsWithChildren<PaginationProps>
>(
  (
    {
      isFilled = true,
      isDisabled = false,
      totalNumberOfPages,
      currentPage,
      onNextClick,
      onPreviousClick,
      onSetPageNumber,
      isPendingData,
    },
    forwardedRef,
  ) => {
    const isFirstPage = currentPage === 1;
    // const isLastPage = currentPage === totalNumberOfPages;

    return (
      <div
        ref={forwardedRef}
        className="flex h-10 items-center justify-center gap-12 px-4"
      >
        <div className="flex items-center gap-4">
          <PaginationButton
            buttonType="utility"
            isFilled={isFilled}
            currentPage={currentPage}
            disabled={currentPage === 1 || isPendingData || isDisabled}
            onClick={valueOrNothing(!isFirstPage, onPreviousClick)}
          >
            <ChevronIcon width={18} height={18} className="rotate-180" />
          </PaginationButton>
          <div className="flex items-center gap-2">
            <PaginationButton
              className={cn({ hidden: !(currentPage > 3) })}
              buttonType="page"
              disabled={isPendingData || isDisabled}
              onClick={valueOrNothing(currentPage !== 1, () =>
                onSetPageNumber(1),
              )}
              isFilled={isFilled}
              currentPage={currentPage}
              pageNumber={1}
            >
              {1}
            </PaginationButton>
            <div className={cn({ hidden: !(currentPage > 3) })}>...</div>
            {/* number */}
            {generatePageNumbers(currentPage, totalNumberOfPages).map(
              (pageNumber) => {
                const isCurrentPage = currentPage === pageNumber;

                return (
                  <PaginationButton
                    buttonType="page"
                    key={pageNumber}
                    disabled={isPendingData || isDisabled}
                    onClick={valueOrNothing(!isCurrentPage, () =>
                      onSetPageNumber(pageNumber),
                    )}
                    isFilled={isFilled}
                    currentPage={currentPage}
                    pageNumber={pageNumber}
                  >
                    {pageNumber}
                  </PaginationButton>
                );
              },
            )}
            <div
              className={cn({
                hidden: !(currentPage < totalNumberOfPages - 2),
              })}
            >
              ...
            </div>
            <PaginationButton
              className={cn({
                hidden: !(currentPage < totalNumberOfPages - 2),
              })}
              buttonType="page"
              disabled={isPendingData || isDisabled}
              onClick={valueOrNothing(currentPage !== totalNumberOfPages, () =>
                onSetPageNumber(totalNumberOfPages),
              )}
              isFilled={isFilled}
              currentPage={currentPage}
              pageNumber={totalNumberOfPages}
            >
              {totalNumberOfPages}
            </PaginationButton>
          </div>

          {/* next arrow */}
          <PaginationButton
            buttonType="utility"
            currentPage={currentPage}
            disabled={
              currentPage === totalNumberOfPages || isPendingData || isDisabled
            }
            onClick={valueOrNothing(
              currentPage !== totalNumberOfPages,
              onNextClick,
            )}
          >
            <ChevronIcon width={18} height={18} />
          </PaginationButton>
        </div>
      </div>
    );
  },
);

Pagination.displayName = "Pagination";

export default Pagination;
